import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import { useToast } from "./use-toast";
import { logger } from "@/lib/logger";
import {
  detectNativePushBridge,
  deleteNativePushSubscription,
  getNativePushState,
  isNativePushPlatform,
  requestNativePushPermission,
  syncNativePushSubscription,
} from "@/lib/native-push";

declare global {
  interface Window {
    OneSignalDeferred?: any[];
    __buukdOneSignalInited?: boolean;
  }
}

function isOneSignalAlreadyInitializedError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return /already initialized/i.test(msg);
}

/**
 * OneSignal v16 throws if `init` runs twice. The CDN / page bootstrap may initialize
 * before our React code runs, while `__buukdOneSignalInited` is still false — treat
 * that case as success and sync our flag.
 */
async function ensureOneSignalWebInitialized(OneSignal: any, appId: string): Promise<void> {
  if (typeof window === "undefined" || !appId) return;
  if (window.__buukdOneSignalInited) return;

  try {
    await OneSignal.init({ appId });
  } catch (e) {
    if (!isOneSignalAlreadyInitializedError(e)) {
      throw e;
    }
  }
  window.__buukdOneSignalInited = true;
}

export function usePushNotifications() {
  const { business } = useBusiness();
  const { toast } = useToast();
  const [isNative, setIsNative] = useState(() => isNativePushPlatform());
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isOneSignalReady, setIsOneSignalReady] = useState(isNative);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);

  // OneSignal App ID (public, safe to embed).
  // Android is the source of truth for the shared Buukd OneSignal app.
  const oneSignalAppId =
    ((import.meta as any)?.env?.VITE_ONESIGNAL_APP_ID as string | undefined) ||
    "3fc56217-03d6-4d0f-b85d-12b3c0993e86";

  // Helper to interact with OneSignal SDK
  const withOneSignal = useCallback(async <T,>(fn: (OneSignal: any) => Promise<T>): Promise<T> => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    return await new Promise<T>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        reject(new Error("OneSignal SDK not loaded. Please refresh and try again."));
      }, 10000); // Increased timeout

      window.OneSignalDeferred!.push(async (OneSignal: any) => {
        window.clearTimeout(timeoutId);
        try {
          const result = await fn(OneSignal);
          resolve(result);
        } catch (e) {
          reject(e);
        }
      });
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const resolveNativeBridge = async () => {
      try {
        const detected = await detectNativePushBridge();
        if (!cancelled) {
          setIsNative(detected);
        }
      } catch (error) {
        logger.warn("Failed to detect native push bridge", error as Error);
      }
    };

    void resolveNativeBridge();

    return () => {
      cancelled = true;
    };
  }, []);

  const ensureNativeMode = useCallback(async (): Promise<boolean> => {
    if (isNative) return true;

    const detected = await detectNativePushBridge();
    if (detected) {
      setIsNative(true);
      setIsSupported(true);
    }

    return detected;
  }, [isNative]);

  // Initialize OneSignal proactively
  useEffect(() => {
    if (isNative) {
      setIsOneSignalReady(true);
      return;
    }

    const initOneSignal = async () => {
      if (!oneSignalAppId || window.__buukdOneSignalInited) {
        setIsOneSignalReady(true);
        return;
      }

      try {
        await withOneSignal(async (OneSignal) => {
          await ensureOneSignalWebInitialized(OneSignal, oneSignalAppId);
          setIsOneSignalReady(true);
        });
      } catch (error) {
        logger.warn("OneSignal initialization failed (will retry on user action)", error as Error);
        // Don't block UI - will retry when user tries to subscribe
        setIsOneSignalReady(false);
      }
    };

    initOneSignal();
  }, [isNative, oneSignalAppId, withOneSignal]);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      if (isNative) {
        setIsSupported(true);
        try {
          const state = await getNativePushState();
          if (state?.permission) {
            setPermission("granted");
          } else {
            setPermission("default");
          }
        } catch (error) {
          logger.warn("Failed to read native push state", error as Error);
          setPermission("default");
        }
        return;
      }

      const supported =
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window;
      setIsSupported(supported);

      if (supported && "Notification" in window) {
        setPermission(Notification.permission);
      }
    };

    void checkSupport();
  }, [isNative]);

  // Check if user is already subscribed
  useEffect(() => {
    const checkSubscription = async () => {
      if (!business || (!isNative && !isSupported)) {
        setIsLoading(false);
        return;
      }

      try {
        const userRes = await supabase.auth.getUser();
        const userId = userRes.data.user?.id;
        if (!userId) {
          setIsSubscribed(false);
          setIsLoading(false);
          return;
        }

        // Check if subscription exists in database
        const { data, error } = await (supabase as any)
          .from("push_subscriptions")
          .select("id")
          .eq("business_id", business.id)
          .eq("user_id", userId)
          .maybeSingle();

        if (error) throw error;
        if (isNative) {
          const synced = await syncNativePushSubscription({
            businessId: business.id,
            userId,
            userAgent: navigator.userAgent,
            requestPermission: false,
          });
          setIsSubscribed(synced.synced);
          setPermission(synced.state?.permission ? "granted" : "default");
          return;
        }

        if (data) {
          setIsSubscribed(true);
          return;
        }

        setIsSubscribed(false);
      } catch (error) {
        logger.error("Error checking push subscription", error as Error);
        setIsSubscribed(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();
  }, [business, isNative, isSupported]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    const shouldUseNative = await ensureNativeMode();

    if (shouldUseNative) {
      try {
        const state = await requestNativePushPermission();
        const granted = !!state?.permission || !!state?.accepted;
        setPermission(granted ? "granted" : "default");
        return granted;
      } catch (error) {
        logger.error("Error requesting native notification permission", error as Error);
        toast({
          title: "Error",
          description: "Failed to request notification permission.",
          variant: "destructive",
        });
        return false;
      }
    }

    if (!isSupported) {
      toast({
        title: "Not supported",
        description: "Push notifications are not supported on this device.",
        variant: "destructive",
      });
      return false;
    }

    if (permission === "granted") {
      return true;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === "granted") {
        return true;
      } else if (result === "denied") {
        toast({
          title: "Permission denied",
          description: "Please enable notifications in your browser settings to receive push notifications.",
          variant: "destructive",
        });
        return false;
      } else {
        // Default - user dismissed
        return false;
      }
    } catch (error) {
      logger.error("Error requesting notification permission", error as Error);
      toast({
        title: "Error",
        description: "Failed to request notification permission.",
        variant: "destructive",
      });
      return false;
    }
  }, [ensureNativeMode, isSupported, permission, toast]);

  // Subscribe to push notifications via OneSignal
  const subscribe = useCallback(async (): Promise<boolean> => {
    const shouldUseNative = await ensureNativeMode();

    if ((!isSupported && !shouldUseNative) || !business) {
      return false;
    }

    if (shouldUseNative) {
      try {
        const userRes = await supabase.auth.getUser();
        const userId = userRes.data.user?.id;
        if (!userId) {
          throw new Error("Not authenticated");
        }

        const result = await syncNativePushSubscription({
          businessId: business.id,
          userId,
          userAgent: navigator.userAgent,
          requestPermission: true,
        });

        if (result.synced === false) {
          if (result.reason === "permission-not-granted") {
            setPermission("default");
            return false;
          }
          throw new Error(result.reason.replace(/-/g, " "));
        }

        setPermission(result.state.permission ? "granted" : "default");
        setIsSubscribed(true);
        return true;
      } catch (error) {
        logger.error("Native OneSignal subscribe error", error as Error);
        toast({
          title: "Enable failed",
          description: error instanceof Error ? error.message : "Failed to enable push notifications. Please try again.",
          variant: "destructive",
        });
        return false;
      }
    }

    // Ensure OneSignal is initialized
    if (!window.__buukdOneSignalInited) {
      try {
        await withOneSignal(async (OneSignal) => {
          await ensureOneSignalWebInitialized(OneSignal, oneSignalAppId);
          setIsOneSignalReady(true);
        });
      } catch (error) {
        logger.error("Failed to initialize OneSignal", error as Error);
        toast({
          title: "Initialization failed",
          description: "Please refresh the page and try again.",
          variant: "destructive",
        });
        return false;
      }
    }

    const hasPermission = await requestPermission();
    if (!hasPermission) {
      return false;
    }

    try {
      // Get OneSignal player ID
      const playerId = await withOneSignal<string>(async (OneSignal) => {
        // Opt-in to push notifications
        if (OneSignal?.User?.PushSubscription?.optIn) {
          await OneSignal.User.PushSubscription.optIn();
        } else if (OneSignal?.setSubscription) {
          await OneSignal.setSubscription(true);
        }

        // Get player ID
        if (OneSignal?.User?.PushSubscription?.id) {
          return String(OneSignal.User.PushSubscription.id);
        }
        if (OneSignal?.getUserId) {
          const id = await OneSignal.getUserId();
          if (id) return String(id);
        }
        
        throw new Error("Could not get OneSignal player ID");
      });

      // Save subscription to database
      const userRes = await supabase.auth.getUser();
      const userId = userRes.data.user?.id;
      if (!userId) {
        throw new Error("Not authenticated");
      }

      const { error: dbError } = await (supabase as any)
        .from("push_subscriptions")
        .upsert(
          {
            business_id: business.id,
            user_id: userId,
            provider: "onesignal",
            provider_subscription_id: playerId,
            user_agent: navigator.userAgent,
          },
          { onConflict: "business_id,user_id" },
        );

      if (dbError) {
        throw dbError;
      }

      setIsSubscribed(true);
      return true;
    } catch (error) {
      logger.error("OneSignal subscribe error", error as Error);
      toast({
        title: "Enable failed",
        description: error instanceof Error ? error.message : "Failed to enable push notifications. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [business, ensureNativeMode, isSupported, oneSignalAppId, requestPermission, withOneSignal, toast]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!business) {
      return false;
    }

    try {
      const shouldUseNative = await ensureNativeMode();

      if (shouldUseNative) {
        const userRes = await supabase.auth.getUser();
        const userId = userRes.data.user?.id;
        if (!userId) {
          throw new Error("Not authenticated");
        }

        await deleteNativePushSubscription({
          businessId: business.id,
          userId,
        });

        setPermission("default");
        setIsSubscribed(false);
        return true;
      }

      // Opt-out from OneSignal (if initialized)
      if (window.__buukdOneSignalInited) {
        try {
          await withOneSignal(async (OneSignal) => {
            if (OneSignal?.User?.PushSubscription?.optOut) {
              await OneSignal.User.PushSubscription.optOut();
            } else if (OneSignal?.setSubscription) {
              await OneSignal.setSubscription(false);
            }
          });
        } catch (error) {
          logger.warn("Could not opt-out from OneSignal", error as Error);
          // Continue anyway - still delete from database
        }
      }

      // Delete subscription from database
      const userRes = await supabase.auth.getUser();
      const userId = userRes.data.user?.id;
      if (!userId) {
        throw new Error("Not authenticated");
      }

      const { error: dbError } = await (supabase as any)
        .from("push_subscriptions")
        .delete()
        .eq("business_id", business.id)
        .eq("user_id", userId);

      if (dbError) {
        throw dbError;
      }

      setIsSubscribed(false);
      return true;
    } catch (error) {
      logger.error("Error unsubscribing from push notifications", error as Error);
      toast({
        title: "Disable failed",
        description: "Failed to disable push notifications. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [business, ensureNativeMode, withOneSignal, toast]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    isOneSignalReady: isNative || isOneSignalReady || window.__buukdOneSignalInited || false,
    permission,
    subscribe,
    unsubscribe,
  };
}
